<script setup lang="ts">
import type { BlockCardNodeViewProps, InlineCardNodeViewProps } from "./cards/types";
import type { LinkNodeNodeViewProps } from "./types";
import { useQuery } from "@tanstack/vue-query";
import { isNodeSelection } from "@tiptap/core";
import CopyToClipboardTooltipButton from "ls/components/CopyToClipboardTooltipButton.vue";
import SimpleButton from "ls/components/simple-button/SimpleButton.vue";
import { SimpleTooltip } from "ls/components/simple-tooltip";
import { useIsEmbed } from "ls/composables/useIsEmbed";
import { queries } from "ls/features/bookmark/queries";
import { isEmbeddingAllowed, toProjectType } from "ls/features/files/utils";
import { CopyIcon, ExternalLinkIcon, Maximize2Icon, MinusIcon, Play, RectangleHorizontalIcon, UnlinkIcon } from "lucide-vue-next";
import { withoutProtocol } from "ufo";
import { computed } from "vue";

const props = defineProps<LinkNodeNodeViewProps | InlineCardNodeViewProps | BlockCardNodeViewProps>();
const emit = defineEmits<{
  unlink: [];
}>();

const { data: urlMetadata } = useQuery(computed(() => queries.urlMetadata(props.node.attrs.url)));

const maximized = defineModel<boolean>("maximized");
const editMode = defineModel<boolean>("editMode");

const isEmbed = useIsEmbed();

const allowEdits = computed(() => props.editor.isEditable);

const isEmailLink = computed(() => {
  return props.node.attrs.url.startsWith("mailto:");
});

const allowEmbed = computed(() => {
  const fileType = urlMetadata.value?.axureFileType;
  if (!fileType) return true;
  const projectType = toProjectType(fileType);
  return projectType !== undefined && isEmbeddingAllowed(projectType);
});

const allowMaximize = computed(() => !isEmailLink.value && !isEmbed.value && allowEmbed.value);

const isInlineCardEnabled = computed(() => {
  const schema = props.editor.schema;
  return !!schema.nodes.inlineCard;
});

const isBlockCardEnabled = computed(() => {
  const schema = props.editor.schema;
  return !!schema.nodes.blockCard && allowEmbed.value;
});

const canChangeLinkType = computed(() => {
  const schema = props.editor.schema;
  return allowEdits.value && !!schema.nodes.linkNode && (isInlineCardEnabled.value || isBlockCardEnabled.value) && !isEmailLink.value;
});

const urlForCopy = computed(() => {
  const url = props.node.attrs.url;
  if (isEmailLink.value) {
    return withoutProtocol(url);
  }
  return url;
});

function handleEditLink() {
  const pos = props.getPos();
  if (pos === undefined) return false;

  props.editor.commands.setNodeSelection(pos);
  editMode.value = true;
}

function handleClickUnlink() {
  emit("unlink");
}

function handleConvertInlineCardToLink() {
  const pos = props.getPos();
  if (pos === undefined) return false;

  props.editor.commands.setNodeSelection(pos);
  const { selection } = props.editor.state;
  if (!isNodeSelection(selection) || selection.node.type.name === "linkNode") {
    return false;
  }

  const attrs = props.node.attrs;
  if (!attrs) return false;

  props.editor.chain()
    .setLinkNode({
      url: attrs.url,
      text: attrs.text,
    })
    .focus()
    .run();
}

function handleConvertLinkToInlineCard() {
  const pos = props.getPos();
  if (pos === undefined) return false;

  props.editor.commands.setNodeSelection(pos);
  const { selection } = props.editor.state;
  if (!isNodeSelection(selection) || selection.node.type.name === "inlineCard") {
    return false;
  }

  const attrs = props.node.attrs;
  if (!attrs) return false;

  props.editor.chain()
    .setInlineCard({
      url: attrs.url,
      text: attrs.text,
    })
    .focus()
    .run();
}

function handleConvertLinkToBlockCard() {
  const pos = props.getPos();
  if (pos === undefined) return false;

  props.editor.commands.setNodeSelection(pos);
  const { selection } = props.editor.state;
  if (!isNodeSelection(selection) || selection.node.type.name === "blockCard") {
    return false;
  }

  const attrs = props.node.attrs;
  if (!attrs) return false;

  props.editor.chain()
    .setBlockCard({
      url: attrs.url,
      text: attrs.text,
    })
    .focus()
    .run();
}

function handleMaximize() {
  maximized.value = true;
}
</script>

<template>
  <div class="flex flex-row flex-nowrap gap-1">
    <template v-if="canChangeLinkType">
      <SimpleTooltip content="URL">
        <SimpleButton
          size="icon"
          variant="ghost"
          :active="props.node.type.name === 'linkNode'"
          @click="handleConvertInlineCardToLink"
        >
          <MinusIcon class="size-4" />
        </SimpleButton>
      </SimpleTooltip>
      <SimpleTooltip v-if="isInlineCardEnabled" content="Title">
        <SimpleButton
          size="icon"
          variant="ghost"
          :active="props.node.type.name === 'inlineCard'"
          @click="handleConvertLinkToInlineCard"
        >
          <RectangleHorizontalIcon class="size-4" />
        </SimpleButton>
      </SimpleTooltip>
      <SimpleTooltip v-if="isBlockCardEnabled" content="Embed">
        <SimpleButton
          size="icon"
          variant="ghost"
          class="mx-0.5"
          :active="props.node.type.name === 'blockCard'"
          @click="handleConvertLinkToBlockCard"
        >
          <RectangleHorizontalIcon class="absolute size-6 stroke-[1.4px]" />
          <Play class="size-2 fill-current" />
        </SimpleButton>
      </SimpleTooltip>
      <div
        role="separator"
        class="m-1 h-5 w-px border-r border-r-slate-300"
      />
    </template>
    <SimpleButton v-if="allowEdits" variant="ghost" @click="handleEditLink">
      Edit link
    </SimpleButton>
    <CopyToClipboardTooltipButton
      :text="urlForCopy"
    >
      <SimpleButton
        variant="ghost"
      >
        <CopyIcon class="size-4" />
      </SimpleButton>
    </CopyToClipboardTooltipButton>
    <SimpleTooltip v-if="!isEmailLink" content="Open in a new tab">
      <SimpleButton
        variant="ghost"
        :href="props.node.attrs.url"
        target="_blank"
      >
        <ExternalLinkIcon class="size-4" />
      </SimpleButton>
    </SimpleTooltip>
    <SimpleTooltip v-if="allowMaximize" content="Maximize">
      <SimpleButton
        variant="ghost"
        @click.stop="handleMaximize"
      >
        <Maximize2Icon class="size-4" />
      </SimpleButton>
    </SimpleTooltip>
    <SimpleTooltip v-if="allowEdits" content="Unlink">
      <SimpleButton variant="ghost" size="icon" @click="handleClickUnlink">
        <UnlinkIcon class="size-4" />
      </SimpleButton>
    </SimpleTooltip>
  </div>
</template>
